/** @format */

import type { PaperCover } from "@question-bank/shared";
import type { PaperLevelOption } from "./PaperLevelForm";
import { type PaperLevelValues } from "./paperLevel";

export interface PaperLevelCatalog {
	subjects: readonly string[];
	qualifications: readonly PaperLevelOption[];
	tiers: readonly PaperLevelOption[];
	series: readonly PaperLevelOption[];
}

const compact = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Month of the sitting date to Core series labels.
 * January and December are Winter. March and April are Spring.
 * May to August are Summer. September to November are Autumn.
 */
const SEASON_KEYS_BY_MONTH: Record<number, readonly string[]> = {
	1: ["winter", "january"],
	2: ["winter"],
	3: ["spring"],
	4: ["spring"],
	5: ["summer"],
	6: ["summer"],
	7: ["summer"],
	8: ["summer"],
	9: ["autumn"],
	10: ["autumn", "november"],
	11: ["autumn", "november"],
	12: ["winter", "january"],
};

const QUALIFICATION_KEYS: Record<NonNullable<PaperCover["level"]>, readonly string[]> = {
	GCSE: ["gcse"],
	"AS-Level": ["aslevel", "as"],
	"A-Level": ["alevel"],
};

const TIER_KEYS: Record<NonNullable<PaperCover["tier"]>, readonly string[]> = {
	Foundation: ["foundation"],
	Intermediate: ["intermediate"],
	Higher: ["higher"],
};

const matchOption = (options: readonly PaperLevelOption[], keys: readonly string[]): string => {
	for (const key of keys) {
		const found = options.find((option) => compact(option.label) === key || compact(option.value) === key);
		if (found) {
			return found.value;
		}
	}
	return "";
};

const toTitleCase = (value: string): string =>
	value.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const matchSubject = (subjects: readonly string[], printed: string | undefined): string => {
	if (!printed) {
		return "";
	}
	const target = compact(printed);
	const exact = subjects.find((subject) => compact(subject) === target);
	if (exact) {
		return exact;
	}
	if (subjects.length === 0) {
		return toTitleCase(printed);
	}
	const contained = subjects.filter((subject) => {
		const key = compact(subject);
		return key.length >= 4 && (target.includes(key) || key.includes(target));
	});
	return contained.length === 1 ? contained[0] : "";
};

/** Map cover facts onto the paper-details form. Unmatched fields stay blank. */
export const paperLevelFromCover = (cover: PaperCover, catalog: PaperLevelCatalog): PaperLevelValues => {
	const date = cover.date && /^\d{4}-\d{2}-\d{2}$/.test(cover.date) ? cover.date : "";
	const month = date ? Number(date.slice(5, 7)) : 0;

	return {
		paperCode: cover.paperCode ?? "",
		subject: matchSubject(catalog.subjects, cover.subject),
		qualification: cover.level ? matchOption(catalog.qualifications, QUALIFICATION_KEYS[cover.level]) : "",
		tier: cover.tier ? matchOption(catalog.tiers, TIER_KEYS[cover.tier]) : "",
		season: month ? matchOption(catalog.series, SEASON_KEYS_BY_MONTH[month] ?? []) : "",
		year: date.slice(0, 4),
		date,
	};
};

/** Fill only fields the user has left blank, so a later read does not replace a choice. */
export const mergeBlankPaperLevel = (current: PaperLevelValues, next: PaperLevelValues): PaperLevelValues => {
	const merged = { ...current };
	(Object.keys(next) as (keyof PaperLevelValues)[]).forEach((key) => {
		if (!current[key] && next[key]) {
			merged[key] = next[key];
		}
	});
	return merged;
};
